import { useState, useEffect } from 'react';

import { getDistrictUrl } from 'utils/zoningDistrictUtils';
import { getBuildingClassification } from 'utils/buildingClassifications';
import { display, displayLotSize } from 'utils/display';
import { API_URL } from 'data/services/apiConstants';

import OutboundLink from 'components/OutboundLink';
import Tooltip from 'components/Tooltip';

import './BuildingInfoTableV2.css';

function BuildingInfoTableV2({ propertyData, zoningData }) {
	// State for sidewalk width estimate
	const [sidewalkWidthEstimate, setSidewalkWidthEstimate] = useState(null);
	const [sidewalkLoading, setSidewalkLoading] = useState(false);
	const [isLandmarkExpanded, setIsLandmarkExpanded] = useState(false);

	const toggleLandmark = () => {
		setIsLandmarkExpanded(!isLandmarkExpanded);
	};

	// Fetch sidewalk data and calculate average width
	useEffect(() => {
		if (propertyData?.id) {
			fetchSidewalkWidthEstimate(propertyData.id);
		}
	}, [propertyData?.id]);

	const fetchSidewalkWidthEstimate = async (propertyId) => {
		setSidewalkLoading(true);
		try {
			const response = await fetch(
				`${API_URL}/api/properties/${propertyId}/sidewalk-widths`,
			);
			if (!response.ok) throw new Error('Failed to fetch sidewalk data');
			const data = await response.json();

			let totalWidth = 0;
			let segmentCount = 0;

			data.sidewalks.forEach((sidewalk) => {
				if (sidewalk.width_segments) {
					sidewalk.width_segments.forEach((segment) => {
						totalWidth += segment.properties.width;
						segmentCount++;
					});
				}
			});

			const averageWidth = segmentCount > 0 ? totalWidth / segmentCount : null;
			setSidewalkWidthEstimate(averageWidth);
		} catch (error) {
			console.error('Error fetching sidewalk width estimate:', error);
			setSidewalkWidthEstimate(null);
		} finally {
			setSidewalkLoading(false);
		}
	};


	let zoningMapElement = display(zoningData.zonemap);
	if (propertyData?.zoning_map_number) {
		let mapNum = propertyData.zoning_map_number.toLowerCase();
		let mapUrl = `https://www1.nyc.gov/assets/planning/download/pdf/zoning/zoning-maps/map${mapNum}.pdf`;
		zoningMapElement = (
			<OutboundLink url={mapUrl}>{display(zoningData.zonemap)}</OutboundLink>
		);
	}

	let columnA = [
		<div className="building-info-item">
			<span className="building-info-field">Year Built</span>
			<span className="building-info-value">
				{display(zoningData?.yearbuilt)}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Lot Size</span>
			<span className="building-info-value">{displayLotSize(zoningData)}</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Lot Area</span>
			<span className="building-info-value">
				{display(zoningData?.lotarea?.toLocaleString())}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Gross Floor Feet</span>
			<span className="building-info-value">
				{display(zoningData?.bldgarea?.toLocaleString())}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Roof Height</span>
			<span className="building-info-value">
				{display(propertyData?.roof_height)}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Number of Buildings</span>
			<span className="building-info-value">
				{display(zoningData?.numbldgs)}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Number of Stories</span>
			<span className="building-info-value">
				{display(zoningData?.numfloors)}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Number of Units</span>
			<span className="building-info-value">
				{display(zoningData?.unitstotal)}
			</span>
		</div>,
	];

	let columnB = [
		<div className="building-info-item">
			<span className="building-info-field">Built FAR</span>
			<span className="building-info-value">
				{display(zoningData.builtfar)}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Commercial FAR</span>
			<span className="building-info-value">{display(zoningData.commfar)}</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Residential FAR</span>
			<span className="building-info-value">
				{display(zoningData.residfar)}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Zoning District 1/2</span>
			<span className="building-info-value">
				<OutboundLink url={getDistrictUrl(zoningData.zonedist1)}>
					{display(zoningData.zonedist1)}
				</OutboundLink>
				/
				<OutboundLink url={getDistrictUrl(zoningData.zonedist2)}>
					{display(zoningData.zonedist2)}
				</OutboundLink>
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Zoning District 3/4</span>
			<span className="building-info-value">
				{zoningData?.zonedist3 || zoningData?.zonedist4 ? (
					<>
						<OutboundLink url={getDistrictUrl(zoningData.zonedist3)}>
							{display(zoningData.zonedist3)}
						</OutboundLink>
						/
						<OutboundLink url={getDistrictUrl(zoningData.zonedist4)}>
							{display(zoningData.zonedist4)}
						</OutboundLink>
					</>
				) : (
					'-/-'
				)}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Commercial Overlay 1/2</span>
			<span className="building-info-value">
				{zoningData?.overlay1 || zoningData?.overlay2 ? (
					<>
						<OutboundLink
							url={
								'https://www.nyc.gov/site/planning/zoning/districts-tools/c1-c2-overlays.page'
							}
						>
							{display(zoningData.overlay1)}
						</OutboundLink>
						/
						<OutboundLink
							url={
								'https://www.nyc.gov/site/planning/zoning/districts-tools/c1-c2-overlays.page'
							}
						>
							{display(zoningData.overlay2)}
						</OutboundLink>
					</>
				) : (
					'-/-'
				)}
			</span>
		</div>,

		<div className="building-info-item">
			<span className="building-info-field">Sidewalk Width</span>
			<span className="building-info-value">
				{sidewalkLoading
					? 'Loading...'
					: sidewalkWidthEstimate !== null
						? `~${Math.round(sidewalkWidthEstimate)} ft`
						: 'N/A'}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Building Frontage</span>
			<span className="building-info-value">
				{display(zoningData?.bldgfront)}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Building Depth</span>
			<span className="building-info-value">
				{display(zoningData?.bldgdepth)}
			</span>
		</div>,
	];

	let landmarkData = propertyData?.od_landmark_buildings?.[0] || {};

	// Landmark details stay collapsed under the Landmark row, as on master
	let landmarkRows = [
		<div
			className="building-info-item landmark-row landmark-toggle-row"
			onClick={toggleLandmark}
		>
			<span className="building-info-field">
				<OutboundLink url="https://www1.nyc.gov/site/lpc/designations/designation-reports.page">
					Landmark
				</OutboundLink>
				<i className={`bi bi-chevron-${isLandmarkExpanded ? 'up' : 'down'}`} />
			</span>
			<span className="building-info-value">
				{display(landmarkData?.hist_dist || propertyData?.landmark || null)}
			</span>
		</div>,
	];

	if (isLandmarkExpanded) {
		landmarkRows = [
			...landmarkRows,
			<div className="building-info-item landmark-row">
				<span className="building-info-field">Architect</span>
				<span className="building-info-value">
					{display(landmarkData?.arch_build || null)}
				</span>
			</div>,
			<div className="building-info-item landmark-row">
				<span className="building-info-field">Primary Style</span>
				<span className="building-info-value">
					{display(landmarkData?.style_prim || null)}
				</span>
			</div>,
			<div className="building-info-item landmark-row">
				<span className="building-info-field">Building Material</span>
				<span className="building-info-value">
					{display(landmarkData?.mat_prim || null)}
				</span>
			</div>,
			<div className="building-info-item landmark-row">
				<span className="building-info-field">Original Use</span>
				<span className="building-info-value">
					{display(landmarkData?.build_type || null)}
				</span>
			</div>,
		];
	}

	let columnC = [
		...landmarkRows,
		<Tooltip text={`${getBuildingClassification(zoningData?.bldgclass)}`}>
			<div className="building-info-item">
				<span className="building-info-field">Building Class</span>
				<span className="building-info-value">
					<OutboundLink
						url={`https://www.nyc.gov/assets/finance/jump/hlpbldgcode.html#${zoningData?.bldgclass?.[0]}`}
					>
						{display(zoningData?.bldgclass)}
					</OutboundLink>
				</span>
			</div>
		</Tooltip>,
		<div className="building-info-item">
			<span className="building-info-field">Zoning Map</span>
			<span className="building-info-value">{zoningMapElement}</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">Land Use</span>
			<span className="building-info-value">{display(zoningData.landuse)}</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">HPD Multiple Dwelling</span>
			<span className="building-info-value">
				{propertyData?.hpd_multiple_dwelling === true ||
				propertyData?.hpd_multiple_dwelling === 1 ||
				propertyData?.hpd_multiple_dwelling === 'Yes'
					? 'Yes'
					: 'No'}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">SRP Restricted</span>
			<span className="building-info-value">
				{propertyData?.sro_restricted === true ||
				propertyData?.sro_restricted === 1 ||
				propertyData?.sro_restricted === 'Yes'
					? 'Yes'
					: 'No'}
			</span>
		</div>,
		<div className="building-info-item">
			<span className="building-info-field">OER</span>
			<span className="building-info-value">
				{propertyData?.environmental_restrictions
					? propertyData.environmental_restrictions
					: 'N/A'}
			</span>
		</div>,
	];

	return (
		<div className="rd-building-info">
			<div className="building-info-flex-wrapper">
				<div>
					<div className="mb-3 building-info-card-header">
						<div>
							<h2 className="building-info-card-title">Property Information</h2>
							{zoningData?.ownername ? (
								<div className="owner-name-subheader">
									Owner: {zoningData.ownername}
								</div>
							) : null}
						</div>
					</div>
					<div className="rd-building-info-col-container">
						<div className="building-info-col">
							{columnA.map((item, i) => (
								<div className="building-info-item-wrapper" key={i}>
									{item}
								</div>
							))}
						</div>
						<div className="building-info-col">
							{columnB.map((item, i) => (
								<div className="building-info-item-wrapper" key={i}>
									{item}
								</div>
							))}
						</div>
						<div className="building-info-col">
							{columnC.map((item, i) => (
								<div className="building-info-item-wrapper" key={i}>
									{item}
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default BuildingInfoTableV2;
